// File: app/src/utils/sessionFilters.js
//
// Narrowing the Activity list.
//
// The history panel used to render every session it had fetched, against a
// hard cap of 100 — so it grew without limit until the cap bit, at which point
// older sessions became unreachable rather than merely distant. Paging fixes
// the second problem; a filter is what makes the first one stop mattering,
// because "the last twenty" is only a useful default if you can ask for
// something else.
//
// Two callers, one vocabulary. A signed-in user filters on the server (the
// history lives in Postgres and there is no reason to ship it all down to
// search it); a signed-out one filters the localStorage backup in the browser.
// Both read this module, so "matches" cannot come to mean two different things
// depending on whether you happen to be logged in.
//
// Pure and React-free so it can be unit-tested directly.

/**
 * Date windows offered in the filter bar.
 *
 * `days: null` is All time — the default, deliberately: paging alone bounds
 * the list, so a first visit shows the same totals it always did rather than
 * silently reporting a subset of the user's training as their whole history.
 */
export const DATE_RANGES = [
  { value: "all", label: "All time", days: null },
  { value: "30d", label: "Last 30 days", days: 30 },
  { value: "3m", label: "Last 3 months", days: 90 },
  { value: "12m", label: "Last 12 months", days: 365 },
];

export const DEFAULT_RANGE = "all";

export const EMPTY_FILTER = { q: "", range: DEFAULT_RANGE };

const rangeDays = (range) =>
  DATE_RANGES.find((r) => r.value === range)?.days ?? null;

/** True when the filter would narrow anything — drives the "Clear" affordance. */
export const isFilterActive = (filter) =>
  !!String(filter?.q || "").trim() || rangeDays(filter?.range) != null;

/**
 * The cutoff a range implies, as an ISO string, or null for All time.
 *
 * Counted back from the start of today rather than from this instant, so
 * "Last 30 days" means thirty calendar days and doesn't quietly drop a session
 * from four weeks ago because it happened in the evening.
 */
export const rangeStart = (range, now = new Date()) => {
  const days = rangeDays(range);
  if (days == null) return null;
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  midnight.setDate(midnight.getDate() - days);
  return midnight.toISOString();
};

/** Filter state -> the query params `api.getSessions`/`getSessionStats` take. */
export const buildSessionQuery = (filter, now = new Date()) => {
  const query = {};
  const term = String(filter?.q || "").trim();
  if (term) query.q = term;
  const from = rangeStart(filter?.range, now);
  if (from) query.from = from;
  return query;
};

/**
 * Every label in a plan tree, flattened.
 *
 * This is the client-side half of the jsonpath extraction the SQL does
 * (`$.**.label`). Matching labels rather than the whole serialised plan is the
 * point: JSON.stringify of a plan contains its keys, so searching "rest" or
 * "work" against the raw text would match every session ever saved.
 */
const collectLabels = (node, out = []) => {
  if (!node || typeof node !== "object") return out;
  if (typeof node.label === "string") out.push(node.label);
  if (Array.isArray(node.children)) {
    for (const child of node.children) collectLabels(child, out);
  }
  return out;
};

/** Does one session match a search term? Mirrors the server's WHERE clause. */
export const sessionMatchesTerm = (session, term) => {
  const needle = String(term || "").trim().toLowerCase();
  if (!needle) return true;
  const haystacks = [
    session?.workout_type || "",
    session?.notes || "",
    ...collectLabels(session?.plan_data),
  ];
  return haystacks.some((h) => String(h).toLowerCase().includes(needle));
};

/**
 * Apply a filter to an in-memory list — the signed-out localStorage path.
 *
 * Same order the server returns (newest first), so the two paths page
 * identically.
 */
export const filterSessionsLocally = (sessions, filter, now = new Date()) => {
  const list = Array.isArray(sessions) ? sessions : [];
  const term = String(filter?.q || "").trim();
  const from = rangeStart(filter?.range, now);
  const fromMs = from ? new Date(from).getTime() : null;

  return list
    .filter((s) => {
      if (fromMs != null) {
        const startedMs = new Date(s?.started_at).getTime();
        // A session with an unreadable date is kept rather than silently
        // dropped: losing a row to a filter is worse than showing it early.
        if (Number.isFinite(startedMs) && startedMs < fromMs) return false;
      }
      return sessionMatchesTerm(s, term);
    })
    .sort(
      (a, b) =>
        new Date(b?.started_at).getTime() - new Date(a?.started_at).getTime(),
    );
};

/**
 * The stat-tile totals for an in-memory list — the local equivalent of
 * GET /api/sessions/stats.
 */
export const summariseSessions = (sessions) => {
  const list = Array.isArray(sessions) ? sessions : [];
  const totalMinutes = list.reduce(
    (sum, s) => sum + (Number(s?.duration_minutes) || 0),
    0,
  );
  return {
    count: list.length,
    total_minutes: totalMinutes,
    avg_minutes: list.length > 0 ? Math.round(totalMinutes / list.length) : 0,
  };
};

/** Every distinct local training day in a list, newest first — streak input. */
export const sessionDates = (sessions) => {
  const days = new Set();
  for (const s of Array.isArray(sessions) ? sessions : []) {
    const d = new Date(s?.started_at);
    if (!Number.isFinite(d.getTime())) continue;
    const pad = (n) => String(n).padStart(2, "0");
    days.add(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
  }
  return [...days].sort().reverse();
};

/** Short human description of what's being shown, for the tile header. */
export const describeFilter = (filter) => {
  const parts = [];
  const range = DATE_RANGES.find((r) => r.value === filter?.range);
  if (range && range.days != null) parts.push(range.label);
  const term = String(filter?.q || "").trim();
  if (term) parts.push(`matching “${term}”`);
  return parts.join(" · ");
};
